import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  BlockStack,
  Button,
  DropZone,
  InlineError,
  InlineStack,
  Text,
  Thumbnail
} from '@shopify/polaris';
import { ordersService } from '../../../services/ordersService';
import { LIMITS, PHOTO_ACCEPT } from '../../../constants/storefrontOrderConstants';

const MAX_MB = Math.round(LIMITS.PHOTO_MAX_BYTES / (1024 * 1024));

// Some browsers (notably Safari on iOS) report an empty file.type for
// HEIC/HEIF photos, so fall back to the file extension when the MIME type
// is missing.
const EXTENSION_TYPES = {
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.webp': 'image/webp',
  '.heic': 'image/heic',
  '.heif': 'image/heif'
};

function resolvesToAcceptedType(file) {
  if (file.type) {
    return PHOTO_ACCEPT.includes(file.type);
  }
  const dot = file.name.lastIndexOf('.');
  if (dot === -1) {
    return false;
  }
  const resolved = EXTENSION_TYPES[file.name.slice(dot).toLowerCase()];
  return resolved ? PHOTO_ACCEPT.includes(resolved) : false;
}

const ACCEPT_ATTR = `${PHOTO_ACCEPT.join(',')},.heic,.heif`;

/**
 * Photo uploader for a story entry, mirroring the storefront form's kid
 * photo step. Client-side validation runs BEFORE the upload service is
 * called; every change hands the parent the full next photo list.
 *
 * @param {Object} props
 * @param {Object[]} props.photos - Current photo list for the entry.
 * @param {Function} props.onChange - Receives the full next photo list.
 * @param {boolean} [props.disabled]
 * @returns {JSX.Element}
 */
export default function PhotoUploader({ photos, onChange, disabled = false }) {
  const [error, setError] = useState('');
  // File objects are kept out of the entry shape but are needed for Retry.
  const filesRef = useRef({});
  const photosRef = useRef(photos);

  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);

  const commit = useCallback(
    nextPhotos => {
      photosRef.current = nextPhotos;
      onChange(nextPhotos);
    },
    [onChange]
  );

  const patchPhoto = useCallback(
    (photoId, patch) => {
      commit(
        photosRef.current.map(photo =>
          photo.id === photoId ? { ...photo, ...patch } : photo
        )
      );
    },
    [commit]
  );

  const startUpload = useCallback(
    (photoId, file) => {
      ordersService
        .uploadOrderPhoto(file)
        .then(url => {
          patchPhoto(photoId, { status: 'done', url, error: undefined });
        })
        .catch(uploadError => {
          patchPhoto(photoId, {
            status: 'error',
            error: uploadError?.message || 'Photo upload failed. Please try again.'
          });
        });
    },
    [patchPhoto]
  );

  const handleDrop = useCallback(
    files => {
      if (disabled) {
        return;
      }
      setError('');
      const invalidMessages = [];

      // Validate every dropped file ourselves so both wrong types and
      // oversized files get an inline message before any service call.
      // eslint-disable-next-line no-restricted-syntax
      for (const file of Array.from(files || [])) {
        if (photosRef.current.length >= LIMITS.MAX_PHOTOS) {
          invalidMessages.push(
            `You can upload up to ${LIMITS.MAX_PHOTOS} photos per story.`
          );
          break;
        }

        const typeOk = resolvesToAcceptedType(file);
        const sizeOk = file.size <= LIMITS.PHOTO_MAX_BYTES;
        if (!typeOk || !sizeOk) {
          invalidMessages.push(
            `"${file.name}" is not supported. Please upload a JPEG, PNG, WebP, HEIC or HEIF image up to ${MAX_MB} MB.`
          );
          // eslint-disable-next-line no-continue
          continue;
        }

        const photoId = `photo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        filesRef.current[photoId] = file;
        const previewUrl =
          typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function'
            ? URL.createObjectURL(file)
            : '';

        commit([
          ...photosRef.current,
          {
            id: photoId,
            status: 'uploading',
            name: file.name,
            previewUrl
          }
        ]);
        startUpload(photoId, file);
      }

      if (invalidMessages.length > 0) {
        setError(invalidMessages.join(' '));
      }
    },
    [commit, disabled, startUpload]
  );

  const handleRemove = useCallback(
    photoId => {
      const photo = photosRef.current.find(item => item.id === photoId);
      if (photo && photo.previewUrl && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(photo.previewUrl);
      }
      delete filesRef.current[photoId];
      commit(photosRef.current.filter(item => item.id !== photoId));
    },
    [commit]
  );

  const handleRetry = useCallback(
    photoId => {
      const file = filesRef.current[photoId];
      if (!file) {
        return;
      }
      patchPhoto(photoId, { status: 'uploading', error: undefined });
      startUpload(photoId, file);
    },
    [patchPhoto, startUpload]
  );

  return (
    <BlockStack gap="200">
      <DropZone
        accept={ACCEPT_ATTR}
        allowMultiple
        disabled={disabled}
        onDrop={handleDrop}
      >
        <DropZone.FileUpload />
      </DropZone>
      {error ? <InlineError fieldID="photo-upload-error" message={error} /> : null}
      {photos.length > 0 ? (
        <BlockStack gap="100">
          {photos.map(photo => (
            <InlineStack key={photo.id} gap="300" blockAlign="center">
              <Thumbnail
                source={photo.previewUrl || photo.url || ''}
                alt={photo.name || 'Kid photo'}
                size="small"
              />
              <Text variant="bodySm" as="span">
                {photo.name || 'Photo'}
                {photo.status === 'uploading' ? ' — uploading…' : ''}
                {photo.status === 'error' && photo.error ? ` — ${photo.error}` : ''}
              </Text>
              {photo.status === 'error' ? (
                <Button plain onClick={() => handleRetry(photo.id)}>
                  Retry
                </Button>
              ) : null}
              <Button
                plain
                destructive
                disabled={disabled}
                onClick={() => handleRemove(photo.id)}
              >
                Remove
              </Button>
            </InlineStack>
          ))}
        </BlockStack>
      ) : null}
    </BlockStack>
  );
}
